import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import moment from "moment";
import { Icon as Iconify } from "@iconify/react";

import { useAuth } from "@/hooks/use-auth";
import { getMeetingById } from "@/api/services";
import { getEarningsSessions } from "@/api/earnings";
import { client } from "@/api";
import { tokenFromMeetingURL, inCallSegmentForMode, joinWindowFor } from "@/utils/meeting";

/**
 * AppointmentDetailsPage
 *
 * Details view for a single scheduled meeting. Reached from both
 * AppointmentTable (desktop list) and AppointmentGrid (cards).
 *
 * Data flow:
 *   1. Read meetingId from URL :param
 *   2. Fetch the populated meeting via /api/meeting/by-id/:id
 *   3. If status === completed, also fetch the matching earning record
 *      from /api/astrologer/earnings/sessions so we can show actual ₹
 *      (not just the duration × rate approximation that the list shows).
 *
 * Action buttons depend on status:
 *   pending  + in 10-min join window → Consult Now (deep-links into
 *                                       the in-call screen)
 *   pending  + not in join window    → Cancel button (calls
 *                                       /api/meeting/cancel)
 *   completed                        → View Earnings (deep-links to the
 *                                       earnings dashboard)
 *   cancelled / expired              → no actions (read-only view)
 */
export default function AppointmentDetailsPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { astrologer } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [earning, setEarning] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!meetingId) {
      setError("Missing appointment ID");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const m = await getMeetingById(meetingId);
        if (cancelled) return;
        if (!m) {
          setError("Could not load appointment details");
          setIsLoading(false);
          return;
        }
        setMeeting(m);

        // For completed meetings, fetch the matching earning record so
        // we can show actual ₹ rather than the list's naive estimate.
        // Best-effort: if the earnings call fails, we fall back to the
        // estimate. We pull a single recent page and find by meetingId
        // — the server doesn't have a by-meeting earnings endpoint yet.
        if (
          m.status === "completed" &&
          astrologer?.astrologerId &&
          astrologer?.accessToken
        ) {
          const { data: earningsData } = await getEarningsSessions(
            astrologer.astrologerId,
            astrologer.accessToken,
            { page: 1, limit: 100 }
          );
          if (cancelled) return;
          // Earnings endpoint returns sessions; each session references
          // a Call OR a Meeting. We match on the sessionId / meetingId
          // pair the server stores. The shape varies between providers
          // so we defensively check both common field names.
          const sessions =
            earningsData?.sessions ||
            earningsData?.earnings ||
            earningsData ||
            [];
          // Server's getEarningsSessions $groups rows by sessionId and
          // projects it as `sessionId`. For scheduled meetings the
          // BillingSession.sessionId is `String(meeting._id)`, so we
          // match on that. The two extra fallbacks are defensive in
          // case the API shape changes.
          const meetingIdStr = String(m._id);
          const match = Array.isArray(sessions)
            ? sessions.find(
                (s) =>
                  String(s.sessionId || "") === meetingIdStr ||
                  String(s.meetingId || "") === meetingIdStr ||
                  String(s.meeting?._id || "") === meetingIdStr
              )
            : null;
          if (match) setEarning(match);
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[AppointmentDetails] load failed:", err?.message);
        setError("Could not load appointment details");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId, astrologer?.astrologerId, astrologer?.accessToken]);

  // Compose a Date object from the (date, time) pair on the meeting.
  // Wrapped in useMemo so the in-window check doesn't recompute on
  // every render but does follow `meeting` changes.
  const meetingDateTime = useMemo(() => {
    if (!meeting?.date || !meeting?.time) return null;
    const [hour, minute] = meeting.time.split(":").map(Number);
    return moment(meeting.date).hour(hour).minute(minute).toDate();
  }, [meeting]);

  // In-window check pulls from the shared utility so the table, grid,
  // and details all use the same 10-min-before / 60-min-after policy.
  // Server's validateMeetingToken still gates the actual Agora-token
  // mint, so the UI bound is just about which button to render.
  const { canJoin: isJoinWindowOpen, hint: joinWindowHint } = useMemo(
    () => joinWindowFor(meetingDateTime, meeting?.status),
    [meetingDateTime, meeting?.status]
  );

  const handleCancel = async () => {
    if (!meetingId || isCancelling) return;
    if (!confirm("Cancel this appointment? The devotee will be notified.")) {
      return;
    }
    setIsCancelling(true);
    try {
      // cancelledBy='astrologer' so the user push reads "your astrologer
      // cancelled" + prompts a rebook flow on their side.
      await client.put("/api/meeting/cancel", {
        meetingId,
        cancelledBy: "astrologer",
      });
      // Refresh local state — bounce back to the list since there's not
      // much to see on a cancelled detail view. The live route is
      // mounted at /schedule/appointments (D5 activation — see
      // routes/sidebar/index.jsx).
      navigate("/schedule/appointments");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[AppointmentDetails] cancel failed:", err?.message);
      alert("Could not cancel the appointment. Please try again.");
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col p-6">
        <BackButton />
        <div className="mt-6 flex justify-center items-center h-64">
          <Iconify
            icon="line-md:loading-loop"
            className="text-4xl text-orange-500"
          />
        </div>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="flex flex-col p-6">
        <BackButton />
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error || "Could not load appointment details"}
        </div>
      </div>
    );
  }

  const modeLabel =
    meeting.mode === "chat"
      ? "Chat"
      : meeting.mode === "call"
      ? "Voice call"
      : "Video call";

  const routeSegment = inCallSegmentForMode(meeting.mode);

  // The in-call route param is the encrypted meeting token, not the
  // raw _id. Server's validateMeetingToken decrypts and confirms the
  // join window; passing the _id directly would fail decryption.
  // joinToken is "" when the meeting has no meetingURL — in that case
  // we keep the Consult Now button disabled.
  const joinToken = tokenFromMeetingURL(meeting.meetingURL);

  // Status pill styling — same vocabulary as the table.
  const statusPillClass =
    {
      pending: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      expired: "bg-gray-100 text-gray-700",
    }[meeting.status] || "bg-gray-100 text-gray-700";

  // Pull the durations + earnings the right way. Server-truth for paid
  // amount is the AstrologerEarning ledger (rolled up by sessionId in
  // getEarningsSessions); fall back to a rate-based estimate only if
  // the ledger lookup didn't find a match. Field name on the rolled-up
  // session row is `netAmount` (astrologer's net after commission);
  // the extra fallbacks defend against API shape changes.
  const durationSec = meeting.duration || 0;
  const durationLabel = formatDuration(durationSec);
  const rate = astrologer?.personalInformation?.rate || 0;
  const actualEarning =
    earning?.netAmount ??
    earning?.astrologerAmount ??
    earning?.amount ??
    earning?.totalEarning ??
    null;
  const estimatedEarning = Math.ceil(durationSec / 60) * rate;

  return (
    <div className="flex flex-col p-6 gap-6 max-w-4xl">
      <BackButton />

      {/* Header card — user identity + status */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <UserAvatar
              name={meeting.user?.name || "User"}
              imageUrl={meeting.user?.picture}
            />
            <div>
              <div className="text-xl font-semibold text-gray-900">
                {meeting.user?.name || "User"}
              </div>
              <div className="text-sm text-gray-500 mt-0.5">
                {meeting.user?.email || ""}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusPillClass}`}>
                  {meeting.status}
                </span>
                <span className="text-xs text-gray-500">
                  Ref {String(meeting._id).slice(-8)}
                </span>
              </div>
            </div>
          </div>

          {/* Primary action — depends on status */}
          <div className="flex flex-col gap-2 items-end">
            {meeting.status === "pending" && isJoinWindowOpen && joinToken && (
              <Link to={`/connect/schedule/${routeSegment}/${joinToken}`}>
                <button className="rounded-full bg-orange-600 hover:bg-orange-700 px-5 py-2.5 text-sm font-bold text-white shadow-md">
                  Consult Now
                </button>
              </Link>
            )}
            {meeting.status === "pending" && isJoinWindowOpen && !joinToken && (
              <button
                disabled
                className="rounded-full bg-orange-300 px-5 py-2.5 text-sm font-bold text-white cursor-not-allowed"
                title="Meeting link unavailable. Contact support."
              >
                Consult Now
              </button>
            )}
            {meeting.status === "pending" && !isJoinWindowOpen && (
              <button
                disabled={isCancelling}
                onClick={handleCancel}
                className="rounded-full border border-red-300 text-red-600 hover:bg-red-50 px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                {isCancelling ? "Cancelling…" : "Cancel appointment"}
              </button>
            )}
            {meeting.status === "completed" && (
              <Link to="/earnings">
                <button className="rounded-full bg-gray-100 hover:bg-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-800">
                  View earnings
                </button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Booking facts */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-200 p-6">
        <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Booking
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Fact label="Mode" value={modeLabel} icon="lucide:headphones" />
          <Fact
            label="Scheduled for"
            value={
              meetingDateTime
                ? moment(meetingDateTime).format("ddd, MMM D · h:mm A")
                : "—"
            }
            icon="lucide:calendar-clock"
          />
          <Fact label="Type" value={meeting.type || "General Consultation"} icon="lucide:tag" />
          <Fact label="Rate" value={`₹${rate}/min`} icon="lucide:badge-indian-rupee" />
        </div>
      </div>

      {/* Conditional sections by status */}
      {meeting.status === "pending" && !isJoinWindowOpen && meetingDateTime && (
        <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 text-sm text-orange-900">
          <div className="flex items-start gap-2">
            <Iconify icon="lucide:info" className="text-base mt-0.5 shrink-0" />
            <div>
              {joinWindowHint && joinWindowHint !== "Join window closed"
                ? `${joinWindowHint}. The Consult Now button enables 10 minutes before the scheduled time.`
                : joinWindowHint === "Join window closed"
                ? "The join window for this appointment has closed."
                : "The Consult Now button enables 10 minutes before the scheduled time."}{" "}
              Until then, you can still cancel from this screen if needed.
            </div>
          </div>
        </div>
      )}

      {meeting.status === "completed" && (
        <div className="rounded-2xl bg-white shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Outcome
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fact label="Duration" value={durationLabel} icon="lucide:timer" />
            <Fact
              label={actualEarning != null ? "Earnings" : "Earnings (est.)"}
              value={`₹${Math.round(actualEarning ?? estimatedEarning)}`}
              icon="lucide:wallet"
            />
          </div>
          {actualEarning == null && (
            <div className="mt-3 text-xs text-gray-500">
              Showing an estimate based on duration × rate. Actual paid amount may
              differ if the call was paused or had connectivity gaps. See the
              earnings dashboard for the final figure.
            </div>
          )}
        </div>
      )}

      {meeting.status === "cancelled" && (
        <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
          This appointment was cancelled. The slot is freed up on your
          calendar and the devotee has been notified.
        </div>
      )}
    </div>
  );
}

/* ─── Small inline components ───────────────────────────────────── */

const BackButton = () => (
  <button
    className="bg-orange-100 hover:bg-orange-200 p-3 rounded-xl w-fit text-orange-700"
    onClick={() => window.history.back()}
  >
    <Iconify icon="akar-icons:arrow-left" className="text-xl" />
  </button>
);

const UserAvatar = ({ name, imageUrl }) => {
  const initial = (name || "U").charAt(0).toUpperCase();
  return (
    <div className="w-16 h-16 rounded-full overflow-hidden bg-orange-200 flex items-center justify-center text-2xl font-bold text-orange-700 shrink-0">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        initial
      )}
    </div>
  );
};

const Fact = ({ label, value, icon }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
      <Iconify icon={icon} className="text-lg" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
        {label}
      </div>
      <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  </div>
);

/* ─── Utilities ─────────────────────────────────────────────────── */

const formatDuration = (durationInSeconds) => {
  const total = parseInt(durationInSeconds, 10) || 0;
  if (total === 0) return "0 min 0 sec";
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes} min ${seconds} sec`;
};
